// 영어 버전 생성 서비스
import { supabase } from '../../lib/supabase';
import { chatCompletion } from '../apiStatus';

/**
 * 영어 버전 업데이트 필요 여부 확인
 * @param {string} sessionId - 세션 ID
 * @returns {Object} { needsUpdate: boolean, lastUpdated: string, englishCreatedAt: string }
 */
export const checkEnglishVersionUpdateNeeded = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('resumes')
      .select('updated_at, english_version_created_at, english_version')
      .eq('session_id', sessionId)
      .single();

    if (error) {
      console.error('❌ 영어 버전 업데이트 체크 실패:', error);
      return { needsUpdate: false, lastUpdated: null, englishCreatedAt: null };
    }

    const hasEnglishVersion = !!data.english_version;
    const lastUpdated = data.updated_at;
    const englishCreatedAt = data.english_version_created_at;

    // 영어 버전이 없으면 업데이트 불필요
    if (!hasEnglishVersion) {
      return { needsUpdate: false, lastUpdated, englishCreatedAt: null };
    }

    // 영어 버전 생성 시간이 없으면 업데이트 불필요 (새로 생성된 경우)
    if (!englishCreatedAt) {
      return { needsUpdate: false, lastUpdated, englishCreatedAt: null };
    }

    // 한글 버전이 영어 버전보다 나중에 업데이트되었는지 확인
    const lastUpdatedTime = new Date(lastUpdated);
    const englishCreatedTime = new Date(englishCreatedAt);
    const needsUpdate = lastUpdatedTime > englishCreatedTime;

    return { needsUpdate, lastUpdated, englishCreatedAt };
  } catch (error) {
    console.error('❌ 영어 버전 업데이트 체크 중 오류:', error);
    return { needsUpdate: false, lastUpdated: null, englishCreatedAt: null };
  }
};

/**
 * 이력서 데이터를 JSON으로 변환 (수정된 필드만 포함 가능)
 * @param {Object} resumeData - 이력서 데이터
 * @param {string} sessionId - 세션 ID
 * @param {Set} modifiedFields - 수정된 필드 목록 (옵션)
 * @param {boolean} isInitialCreation - 초기 생성 여부
 * @returns {Object} JSON 형태의 이력서 데이터
 */
export const createResumeJSON = (resumeData, sessionId, modifiedFields = null, isInitialCreation = false) => {
  try {
    // 모든 필드를 포함하는 전체 JSON
    const fullResumeJSON = {
      sessionId: sessionId,
      name: resumeData.name || '', // 이름도 번역 대상에 포함
      title: resumeData.title || '',
      summary: resumeData.summary || '',
      key_strengths: resumeData.key_strengths || resumeData.keyStrengths || [],
      experiences: resumeData.experiences || [],
      education: resumeData.education || [],
      certificates: resumeData.certificates || [],
      skills: resumeData.skills || [],
      papers: resumeData.papers || [],
      // 번역이 필요 없는 필드들 (영어 이력서에는 포함되어야 함)
      email: resumeData.email || '',
      phone: resumeData.phone || '',
      social_links: resumeData.social_links || []
    };

    // 초기 생성이면 모든 필드 반환
    if (isInitialCreation) {
      return fullResumeJSON;
    }

    // 수정된 필드가 제공된 경우, 번역이 필요한 필드만 포함
    if (modifiedFields && modifiedFields.size > 0) {
      const filteredJSON = {
        sessionId: sessionId
      };
      
      // 수정된 필드 중 번역이 필요한 필드만 포함
      modifiedFields.forEach(field => {
        if (field === 'keyStrengths' && fullResumeJSON.key_strengths !== undefined) {
          filteredJSON.key_strengths = fullResumeJSON.key_strengths;
        } else if (fullResumeJSON[field] !== undefined) {
          // email, phone, social_links는 번역하지 않으므로 제외
          if (!['email', 'phone', 'social_links'].includes(field)) {
            filteredJSON[field] = fullResumeJSON[field];
          }
        }
      });
      
      return filteredJSON;
    }

    return fullResumeJSON;
  } catch (error) {
    console.error('❌ 이력서 JSON 생성 실패:', error);
    throw new Error('이력서 데이터를 JSON으로 변환하는 중 오류가 발생했습니다.');
  }
};

/**
 * Gemini 2.0 Flash API를 사용하여 영어 이력서 생성
 * @param {Object} resumeJSON - 한국어 이력서 JSON 데이터
 * @returns {Object} 영어로 번역된 이력서 JSON 데이터
 */
export const translateResumeToEnglish = async (resumeJSON) => {
  try {
    const prompt = createTranslationPrompt(resumeJSON);

    const { content: translatedText } = await chatCompletion({
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.1,
      maxTokens: 8192,
      responseFormat: { type: 'json_object' },
    });

    return parseTranslationResponse(translatedText);
  } catch (error) {
    console.error('❌ AI 번역 실패:', error);
    throw new Error(`영어 번역 중 오류가 발생했습니다: ${error.message}`);
  }
};

/**
 * 번역을 위한 프롬프트 생성
 * @param {Object} resumeJSON - 한국어 이력서 JSON
 * @returns {string} 번역 프롬프트
 */
const createTranslationPrompt = (resumeJSON) => {
  return `You are a professional resume translator. Translate the following Korean resume to English while maintaining the exact JSON structure.

IMPORTANT:
- Return ONLY valid JSON, no markdown or extra text
- Translate ALL Korean text to professional English
- Keep all field names unchanged
- Use standard business English terminology
- Convert Korean names to English format (e.g., "이인성" → "Inseong Lee")
- Use official English company names where known

Input JSON:
${JSON.stringify(resumeJSON, null, 2)}

Output only the translated JSON:`;
};

/**
 * Gemini API 응답을 파싱하여 JSON 객체로 변환
 * @param {string} translatedText - Gemini API 응답 텍스트
 * @returns {Object} 파싱된 영어 이력서 JSON
 */
const parseTranslationResponse = (translatedText) => {
  try {
    let jsonText = translatedText.trim();
    
    // 마크다운 코드 블록 제거
    if (jsonText.includes('```')) {
      const match = jsonText.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (match) {
        jsonText = match[1].trim();
      }
    }
    
    // JSON 범위 찾기
    const startIndex = jsonText.indexOf('{');
    const endIndex = jsonText.lastIndexOf('}');
    
    if (startIndex !== -1 && endIndex > startIndex) {
      jsonText = jsonText.substring(startIndex, endIndex + 1);
    }
    
    // JSON 파싱
    const parsedJSON = JSON.parse(jsonText);
    
    // 부분 업데이트인 경우 기본 필드를 추가하지 않음
    // 전체 업데이트인 경우에만 누락된 필드에 기본값 추가
    const hasAllMainFields = ['name', 'title', 'summary'].every(field => field in parsedJSON);
    
    if (hasAllMainFields) {
      // 전체 업데이트로 판단 - 기본 필드 보장
      const defaultFields = {
        sessionId: '',
        name: 'English Version',
        title: 'Professional',
        summary: 'English version of the resume',
        key_strengths: [],
        experiences: [],
        education: [],
        certificates: [],
        skills: [],
        papers: []
      };
      
      // 누락된 필드 추가
      for (const [field, defaultValue] of Object.entries(defaultFields)) {
        if (!(field in parsedJSON)) {
          parsedJSON[field] = defaultValue;
        }
      }
    }
    
    return parsedJSON;
    
  } catch (error) {
    console.error('❌ JSON 파싱 실패:', error);
    console.error('파싱 시도한 텍스트:', translatedText.substring(0, 500));
    
    // 파싱 에러를 상위로 전달하여 사용자에게 재시도 메시지 표시
    throw new Error('영어 이력서 생성 중 오류가 발생했습니다. English 버튼을 다시 클릭해 주세요.');
  }
};

/**
 * 영어 버전을 데이터베이스에 저장 (부분 업데이트 지원)
 * @param {string} sessionId - 세션 ID
 * @param {Object} englishResumeJSON - 영어 이력서 JSON 데이터
 * @param {boolean} isPartialUpdate - 부분 업데이트 여부
 * @param {Object} originalData - 원본 한국어 데이터 (번역 불필요 필드용)
 * @returns {boolean} 저장 성공 여부
 */
export const saveEnglishVersionToDB = async (sessionId, englishResumeJSON, isPartialUpdate = false, originalData = null) => {
  try {    
    const now = new Date();
    // 트리거에 의해 updated_at이 자동 갱신되므로 영어 버전 생성 시간을 1초 후로 설정
    const englishCreatedAt = new Date(now.getTime() + 1000).toISOString(); // 1초 후
    
    let updateData;
    
    if (isPartialUpdate) {
      // 부분 업데이트인 경우 기존 영어 버전을 가져와서 병합
      const { data: existingData, error: fetchError } = await supabase
        .from('resumes')
        .select('english_version')
        .eq('session_id', sessionId)
        .single();
        
      if (fetchError) {
        console.error('❌ 기존 영어 버전 조회 실패:', fetchError);
        throw new Error(`기존 영어 버전 조회 실패: ${fetchError.message}`);
      }
      
      // 기존 데이터와 새 데이터 병합 (sessionId는 제외)
      const { sessionId: _, ...newFields } = englishResumeJSON;
      
      // 번역이 필요 없는 필드들은 원본 데이터에서 가져와서 업데이트
      const nonTranslatableFields = {};
      if (originalData) {
        if (originalData.email) nonTranslatableFields.email = originalData.email;
        if (originalData.phone) nonTranslatableFields.phone = originalData.phone;
        if (originalData.social_links) nonTranslatableFields.social_links = originalData.social_links;
      }
      
      updateData = {
        english_version: {
          ...existingData.english_version,
          ...newFields,
          ...nonTranslatableFields  // 번역 불필요 필드 추가
        },
        english_version_created_at: englishCreatedAt
      };
    } else {
      // 전체 업데이트 - 초기 생성 시에는 모든 필드가 포함됨
      updateData = {
        english_version: englishResumeJSON,
        english_version_created_at: englishCreatedAt
      };
    }
    
    const { data, error } = await supabase
      .from('resumes')
      .update(updateData)
      .eq('session_id', sessionId)
      .select(); // 업데이트된 데이터 반환

    if (error) {
      console.error('❌ DB 저장 실패:', error);
      throw new Error(`데이터베이스 저장 실패: ${error.message}`);
    }

    return true;
  } catch (error) {
    console.error('❌ 영어 버전 저장 중 오류:', error);
    throw new Error(`영어 버전 저장 중 오류가 발생했습니다: ${error.message}`);
  }
};

/**
 * 전체 영어 버전 생성 프로세스
 * @param {Object} resumeData - 이력서 데이터
 * @param {string} sessionId - 세션 ID
 * @param {Function} onProgress - 진행 상황 콜백 함수
 * @param {Set} modifiedFields - 수정된 필드 목록 (옵션)
 * @returns {Object} 생성된 영어 이력서 JSON
 */
export const generateEnglishVersion = async (resumeData, sessionId, onProgress = () => {}, modifiedFields = null) => {
  try {    
    const isPartialUpdate = modifiedFields && modifiedFields.size > 0;
    const isInitialCreation = !resumeData.english_version;
    
    // 1단계: 이력서 JSON 생성 (수정된 필드만 또는 전체)
    onProgress?.('이력서 데이터 준비 중...', 20);
    const resumeJSON = createResumeJSON(resumeData, sessionId, modifiedFields, isInitialCreation);
    
    // 2단계: Gemini API로 영어 번역
    onProgress?.('AI가 영어로 번역하는 중...', 60);
    const englishResumeJSON = await translateResumeToEnglish(resumeJSON);
    
    // 3단계: 데이터베이스에 저장 (파싱 성공 후에만)
    await saveEnglishVersionToDB(sessionId, englishResumeJSON, isPartialUpdate, resumeData);
    
    onProgress?.('영어 버전 생성 완료!', 100);
    
    // 부분 업데이트의 경우 병합된 전체 데이터 반환을 위해 다시 조회
    if (isPartialUpdate) {
      const updatedEnglishVersion = await getEnglishVersion(sessionId);
      return updatedEnglishVersion || englishResumeJSON;
    }
    
    return englishResumeJSON;
    
  } catch (error) {
    console.error('❌ 영어 버전 생성 프로세스 실패:', error);
    
    // 파싱 에러인지 확인하여 적절한 에러 메시지 제공
    if (error.message.includes('영어 이력서 생성 중 오류가 발생했습니다')) {
      // 파싱 에러의 경우 - DB는 업데이트하지 않았으므로 재시도 가능
      throw error;
    } else {
      // 다른 에러의 경우
      throw new Error(`영어 버전 생성 실패: ${error.message}`);
    }
  }
};

/**
 * 영어 버전 데이터를 가져오기
 * @param {string} sessionId - 세션 ID
 * @returns {Object|null} 영어 이력서 JSON 또는 null
 */
export const getEnglishVersion = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('resumes')
      .select('english_version')
      .eq('session_id', sessionId)
      .single();

    if (error) {
      console.error('❌ 영어 버전 조회 실패:', error);
      return null;
    }

    return data?.english_version || null;
  } catch (error) {
    console.error('❌ 영어 버전 조회 중 오류:', error);
    return null;
  }
};
